<!-- src/EmployeeInsightsComponents/BillableGauge.vue -->
<template>
  <div
    class="w-full flex items-center"
    :class="layout === 'horizontal' ? 'justify-start gap-6' : 'justify-center mt-4'"
  >
    <!-- Doughnut chart -->
    <div
      class="relative"
      style="width:120px; height:120px;"
    >
      <canvas ref="canvas" />
      <div
        class="absolute inset-0 flex items-center justify-center text-lg font-semibold text-gray-700"
      >
        <Transition
          name="gauge-percent"
          mode="out-in"
        >
          <span :key="displayPercent">
            {{ displayPercent }}%
          </span>
        </Transition>
      </div>
    </div>

    <!-- Custom legend + weekly target -->
    <div
      class="text-sm text-gray-700"
      :class="layout === 'horizontal' ? 'flex-1 flex items-start gap-6' : 'ml-4 flex flex-col justify-center'"
    >
      <div
        class="flex flex-col items-start"
        :class="layout === 'horizontal' ? 'flex-1' : ''"
      >
        <div
          class="flex flex-col"
          :class="layout === 'horizontal' ? 'grid grid-cols-2 gap-x-6 gap-y-2' : 'space-y-2'"
        >
          <TransitionGroup
            name="legend-item"
            tag="div"
            class="contents"
          >
            <div
              v-if="includeClientNonBillable"
              key="combined-billable"
              class="legend-combined flex items-start gap-2"
              :class="layout === 'horizontal' ? 'col-span-2' : ''"
            >
              <span class="block w-6 h-6 bg-sushi rounded-sm mt-1" />
              <div class="leading-tight">
                <div class="text-xs font-semibold text-gray-700">
                  Billable + client non-billable
                </div>
                <div class="text-xs text-gray-500">
                  <span>{{ formatHoursToParts(combinedBillableHrs).hours }}</span><span class="text-[10px] align-super">h</span>
                  <span class="ml-1">{{ formatHoursToParts(combinedBillableHrs).minutes }}</span><span class="text-[10px] align-super">m</span>
                  <span> ({{ combinedBillablePercent }}%)</span>
                </div>
              </div>
            </div>
            <div
              v-else
              key="billable"
              class="legend-billable flex items-start gap-2"
            >
              <span class="block w-6 h-6 bg-sushi rounded-sm mt-1" />
              <div class="leading-tight">
                <div class="text-xs font-semibold text-gray-700">
                  Billable
                </div>
                <div class="text-xs text-gray-500">
                  <span>{{ formatHoursToParts(billableHrs).hours }}</span><span class="text-[10px] align-super">h</span>
                  <span class="ml-1">{{ formatHoursToParts(billableHrs).minutes }}</span><span class="text-[10px] align-super">m</span>
                  <span> ({{ billablePercent }}%)</span>
                </div>
              </div>
            </div>
            <div
              v-if="!includeClientNonBillable"
              key="client-non-billable"
              class="legend-client flex items-start gap-2"
            >
              <span class="block w-6 h-6 bg-brand-blue rounded-sm mt-1" />
              <div class="leading-tight">
                <div class="text-xs font-semibold text-gray-700">
                  Client non-billable
                </div>
                <div class="text-xs text-gray-500">
                  <span>{{ formatHoursToParts(clientNonBillHrs).hours }}</span><span class="text-[10px] align-super">h</span>
                  <span class="ml-1">{{ formatHoursToParts(clientNonBillHrs).minutes }}</span><span class="text-[10px] align-super">m</span>
                  <span> ({{ clientNonBillPercent }}%)</span>
                </div>
              </div>
            </div>
          </TransitionGroup>
          <div class="flex items-start gap-2">
            <span class="block w-6 h-6 bg-[#FBBF24] rounded-sm mt-1" />
            <div class="leading-tight">
              <div class="text-xs font-semibold text-gray-700">
                Internal non-billable
              </div>
              <div class="text-xs text-gray-500">
                <span>{{ formatHoursToParts(internalNonBillHrs).hours }}</span><span class="text-[10px] align-super">h</span>
                <span class="ml-1">{{ formatHoursToParts(internalNonBillHrs).minutes }}</span><span class="text-[10px] align-super">m</span>
                <span> ({{ internalNonBillPercent }}%)</span>
              </div>
            </div>
          </div>
          <div class="flex items-start gap-2">
            <span class="block w-6 h-6 bg-red-500 rounded-sm mt-1" />
            <div class="leading-tight">
              <div class="text-xs font-semibold text-gray-700">
                Unaccounted
              </div>
              <div class="text-xs text-gray-500">
                <span>{{ formatHoursToParts(unaccountHrs).hours }}</span><span class="text-[10px] align-super">h</span>
                <span class="ml-1">{{ formatHoursToParts(unaccountHrs).minutes }}</span><span class="text-[10px] align-super">m</span>
                <span> ({{ unaccountPercent }}%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div :class="layout === 'horizontal' ? 'min-w-[180px]' : 'mt-3'">
        <div class="block text-xs font-medium uppercase tracking-wide text-gray-500">
          Weekly target
        </div>
        <div class="mt-1 flex items-baseline space-x-2">
          <span class="text-sm font-semibold text-gray-800">{{ weeklyTarget }}</span>
          <span class="text-xs text-gray-500">h / week</span>
        </div>
        <p class="mt-1 text-xs text-gray-400">
          Range target: {{ denomDisplay }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import Chart from 'chart.js/auto'
import { useEmployeeInsightsStore } from '@/pinia/employeeInsightsStore'
import {
  computeGaugeTotals,
  formatHoursToParts
} from '@/utils/employeeInsightsCalculations'

const props = defineProps({
  staffId: { type: [String, Number], required: true },
  includeClientNonBillable: { type: Boolean, default: false },
  layout: { type: String, default: 'stack' }
})

const eiStore = useEmployeeInsightsStore()
const canvas = ref(null)
let chartInstance = null

const weeklyTarget = computed(() => eiStore.weeklyTargetForStaff(props.staffId))

const gaugeData = computed(() =>
  computeGaugeTotals(
    eiStore.dailyStatsByStaff?.[props.staffId] || [],
    eiStore.startDate,
    eiStore.endDate,
    weeklyTarget.value
  )
)
const billableHrs = computed(() => gaugeData.value.billable)
const clientNonBillHrs = computed(() => gaugeData.value.clientNonBillable)
const combinedBillableHrs = computed(() => billableHrs.value + clientNonBillHrs.value)
const internalNonBillHrs = computed(() => gaugeData.value.internalNonBillable)
const unaccountHrs = computed(() => gaugeData.value.unaccounted)
const denom = computed(() => gaugeData.value.denom)
const denomDisplay = computed(() => denom.value ? `${denom.value.toFixed(2)}h` : '--')

const billablePercent = computed(() =>
  denom.value ? Math.round((billableHrs.value / denom.value) * 100) : 0
)
const combinedBillablePercent = computed(() =>
  denom.value ? Math.round((combinedBillableHrs.value / denom.value) * 100) : 0
)
const displayPercent = computed(() => {
  if (!denom.value) return 0
  return props.includeClientNonBillable ? combinedBillablePercent.value : billablePercent.value
})
const clientNonBillPercent = computed(() =>
  denom.value ? Math.round((clientNonBillHrs.value / denom.value) * 100) : 0
)
const internalNonBillPercent = computed(() =>
  denom.value ? Math.round((internalNonBillHrs.value / denom.value) * 100) : 0
)
const unaccountPercent = computed(() =>
  denom.value ? Math.round((unaccountHrs.value / denom.value) * 100) : 0
)

function renderChart() {
  if (!canvas.value) return
  const ctx = canvas.value.getContext('2d')
  if (!ctx) return

  const data = props.includeClientNonBillable
    ? [
        combinedBillableHrs.value,
        internalNonBillHrs.value,
        unaccountHrs.value
      ]
    : [
        billableHrs.value,
        clientNonBillHrs.value,
        internalNonBillHrs.value,
        unaccountHrs.value
      ]
  const labels = props.includeClientNonBillable
    ? ['Billable + Client Non-billable','Internal Non-billable','Unaccounted']
    : ['Billable','Client Non-billable','Internal Non-billable','Unaccounted']
  const backgroundColor = props.includeClientNonBillable
    ? ['#83BD48', '#FBBF24', '#EF4444']
    : ['#83BD48', '#00A3E0', '#FBBF24', '#EF4444']

  if (chartInstance) {
    chartInstance.data.labels = labels
    chartInstance.data.datasets[0].data = data
    chartInstance.data.datasets[0].backgroundColor = backgroundColor
    chartInstance.update()
    return
  }

  chartInstance = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels,
      datasets: [{
        data,
        backgroundColor,
        cutout: '70%',
        borderWidth: 0
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        tooltip: { enabled: false },
        legend:  { display: false }
      }
    }
  })
}

onMounted(renderChart)
watch(
  () => [
    gaugeData.value.billable,
    gaugeData.value.clientNonBillable,
    gaugeData.value.internalNonBillable,
    gaugeData.value.unaccounted,
    weeklyTarget.value,
    eiStore.startDate,
    eiStore.endDate,
    props.includeClientNonBillable
  ],
  renderChart
)
</script>

<style scoped>
.gauge-percent-enter-active,
.gauge-percent-leave-active,
.legend-item-enter-active,
.legend-item-leave-active {
  transition: opacity 180ms ease, transform 180ms ease;
}

.gauge-percent-enter-from,
.gauge-percent-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.legend-item-enter-from,
.legend-item-leave-to {
  opacity: 0;
}

.legend-billable.legend-item-enter-from,
.legend-billable.legend-item-leave-to {
  transform: translateX(18px);
}

.legend-client.legend-item-enter-from,
.legend-client.legend-item-leave-to {
  transform: translateX(-18px);
}

.legend-combined.legend-item-enter-from,
.legend-combined.legend-item-leave-to {
  transform: scaleX(0.96);
}
</style>
